import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { getAdminAccessToken, setAdminAccessToken } from '../utils/authToken';

const AdminAuthContext = createContext();

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * The backoffice access token is held IN MEMORY only.
 *
 * It used to live in `localStorage.adminToken`, which meant any script running
 * on this origin could read a platform-admin credential — the highest-privilege
 * token in the product. It is now a module-level variable, refreshed on page
 * load from the httpOnly `zappy_rt_bo` cookie, exactly like the dashboard.
 *
 * The variable itself lives in the LEAF module `utils/authToken`, so any page
 * can read the live token without an import cycle through this context. The
 * legacy key is only ever REMOVED, never written, so a returning admin stops
 * carrying the old readable copy the first time they load the app.
 */

// Cookies must ride along on the refresh call (the backoffice is on a
// different origin from the API).
axios.defaults.withCredentials = true;

/**
 * Single-flight refresh — see the matching note in the client's AuthContext.
 * Refresh tokens rotate, so two concurrent refreshes would make the second one
 * look like a replayed token and revoke the whole family.
 */
let refreshPromise = null;

const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}/api/auth/refresh`, { surface: 'backoffice' }, {
        withCredentials: true,
        _skipAuthRefresh: true,
      })
      .then((response) => response?.data?.token || null)
      .catch(() => null)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

/**
 * Access tokens last 15 minutes. Refresh a little early so a request that
 * leaves the browser as the token expires does not race the server clock.
 */
const ACCESS_TOKEN_REFRESH_SKEW_MS = 60 * 1000;

const accessTokenExpiryMs = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = JSON.parse(atob(padded));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
};

/** True when the in-memory access token is expired or inside the skew window. */
const isAccessTokenStale = (token) => {
  const exp = accessTokenExpiryMs(token);
  // Unreadable exp is not "stale" — a missing token is the logged-out case,
  // and a non-JWT must not trigger a refresh on every request.
  if (!exp) return false;
  return Date.now() >= exp - ACCESS_TOKEN_REFRESH_SKEW_MS;
};

const setAuthHeader = (headers, value) => {
  if (!headers) return;
  if (typeof headers.set === 'function') {
    headers.set('Authorization', value);
    return;
  }
  headers.Authorization = value;
};

const deleteAuthHeader = (headers) => {
  if (!headers) return;
  if (typeof headers.delete === 'function') {
    headers.delete('Authorization');
    return;
  }
  delete headers.Authorization;
  delete headers.authorization;
};

/**
 * Put the fresh token on a retried request.
 *
 * Replacing `headers` with a spread plain object drops Authorization on retry
 * in some axios 1.x AxiosHeaders shapes, so the retry repeats the expired
 * bearer, 401s again, and the screen stays dead. Prefer AxiosHeaders#set.
 */
const stampRetryAuthorization = (original, token) => {
  const headers = original.headers;
  if (headers && typeof headers.set === 'function') {
    headers.set('Authorization', `Bearer ${token}`);
    headers.delete?.('If-None-Match');
    headers.delete?.('If-Modified-Since');
    return;
  }
  original.headers = {
    ...(headers && typeof headers.toJSON === 'function' ? headers.toJSON() : (headers || {})),
    Authorization: `Bearer ${token}`,
  };
  delete original.headers['If-None-Match'];
  delete original.headers['If-Modified-Since'];
  delete original.headers['if-none-match'];
  delete original.headers['if-modified-since'];
};

const applyToken = (value) => {
  setAdminAccessToken(value);
  const current = getAdminAccessToken();
  if (current) {
    axios.defaults.headers.common.Authorization = `Bearer ${current}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mirrors `accessToken` purely so consumers re-render when it changes; the
  // authoritative value is the module variable the interceptor reads.
  const [token, setToken] = useState(null);
  const interceptorRef = useRef(null);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/logout`, { surface: 'backoffice' }, {
        withCredentials: true,
        _skipAuthRefresh: true,
      });
    } catch (error) {
      // Best effort — the local token is dropped either way.
      console.warn('Backoffice logout request failed:', error);
    }
    applyToken(null);
    setToken(null);
    setAdmin(null);
  }, []);

  // A 15-minute access token must not interrupt an admin who left the tab
  // idle. Two layers:
  //   1. Before the request leaves, if the in-memory token is already stale,
  //      refresh it and stamp the live value. A per-request Authorization
  //      header wins over axios defaults, so this interceptor has to overwrite
  //      it — otherwise a caller that read the token a moment earlier sends
  //      the expired bearer and the screen toasts.
  //   2. If the server still answers 401 (or the legacy 403 TOKEN_EXPIRED /
  //      TOKEN_INVALID that admin routes used to return), refresh once and
  //      retry. ADMIN_REQUIRED stays a real 403 and is not refreshed.
  useEffect(() => {
    if (interceptorRef.current !== null) return undefined;

    const requestId = axios.interceptors.request.use(async (config) => {
      if (config._skipAuthRefresh) return config;

      let current = getAdminAccessToken();
      if (current && isAccessTokenStale(current)) {
        const fresh = await refreshAccessToken();
        if (fresh) {
          applyToken(fresh);
          setToken(fresh);
          current = fresh;
        }
      }

      if (!config.headers) config.headers = {};
      if (current) {
        setAuthHeader(config.headers, `Bearer ${current}`);
      } else {
        // Better to send nothing than "Bearer null" / "Bearer undefined".
        deleteAuthHeader(config.headers);
      }
      return config;
    });

    const responseId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config || {};
        const status = error.response?.status;
        const code = error.response?.data?.code;
        const isExpiredOrInvalidToken = status === 401
          || (status === 403 && (code === 'TOKEN_EXPIRED' || code === 'TOKEN_INVALID'));

        if (
          isExpiredOrInvalidToken &&
          !original._skipAuthRefresh &&
          !original._retriedAfterRefresh &&
          // A step-up prompt is not an expired session; refreshing returns the
          // same stale auth_time and fails identically.
          code !== 'REAUTH_REQUIRED'
        ) {
          const fresh = await refreshAccessToken();
          if (fresh) {
            applyToken(fresh);
            setToken(fresh);
            original._retriedAfterRefresh = true;
            stampRetryAuthorization(original, fresh);
            return axios(original);
          }
        }
        return Promise.reject(error);
      }
    );

    interceptorRef.current = { requestId, responseId };
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.request.eject(interceptorRef.current.requestId);
        axios.interceptors.response.eject(interceptorRef.current.responseId);
        interceptorRef.current = null;
      }
    };
  }, []);

  // Warm the session when the admin comes back to the tab, before the click
  // that would otherwise be the first request to discover the token is dead.
  useEffect(() => {
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const current = getAdminAccessToken();
      if (!current || !isAccessTokenStale(current)) return;
      refreshAccessToken().then((fresh) => {
        if (!fresh) return;
        applyToken(fresh);
        setToken(fresh);
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  // Bootstrap: no in-memory token is the normal state on every load, so ask
  // the refresh endpoint before concluding the admin is signed out.
  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      const fresh = getAdminAccessToken() || (await refreshAccessToken());
      if (cancelled) return;

      if (!fresh) {
        applyToken(null);
        setLoading(false);
        return;
      }

      applyToken(fresh);
      setToken(fresh);

      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/me`);
        const user = response.data;
        if (cancelled) return;

        if (user.is_admin) {
          setAdmin(user);
        } else {
          // A valid session that is not a platform admin has no business here.
          await handleLogout();
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        if (!cancelled) await handleLogout();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    checkAuth();
    return () => { cancelled = true; };
  }, [handleLogout]);

  const handleLogin = async (authToken) => {
    try {
      applyToken(authToken);
      setToken(authToken);

      const response = await axios.get(`${API_BASE_URL}/api/auth/me`);
      const user = response.data;

      if (!user.is_admin) {
        throw new Error('Access denied - Admin privileges required');
      }

      setAdmin(user);
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      await handleLogout();
      throw error;
    }
  };

  const value = {
    admin,
    loading,
    isAuthenticated: !!admin,
    login: handleLogin,
    logout: handleLogout,
    token
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export default AdminAuthContext;
